import {useEffect, useRef, useState} from 'react'
import {useFetcher, useOutletContext, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import {useReportAnalyticsEngaging, useReportClickoutToSiteTime, useReportPageInfo} from '~/hooks/analytic-hooks'
import type {RootAndListContext, RootLoaderResponse, SlugLoaderResponse, SortingOption} from '~/types'
import {getSortedProducts} from '~/utils/products-utils'
import ScrollOnTop from '~/components/commons/scroll-on-top'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {cssDomains} from '~/utils/constants'
import FaqSection from '../../components/commons/faq'
import TrustBar from './sections/trust-bar'
import {RelatedProducts} from './related-products/related-products'
import {RecommendedProducts} from './recomended-products/recommended-products'
import {PopularCategories} from './sections/popular-categories'
import ReasoningSection from './sections/reasoning'
import SponsoredProducts from './sponsored-products/sponsored-products'
import CategoryProducts from './category-products/category-products'
import BuyingGuide from './sections/buying-guide'
import {OptinmonsterMetadata} from './optinmonster/optinmonster-metadata'
import RankSection from './sections/rank-section'
import HustonProblemModal from './modals/huston-problem-modal'
import TopPicks from './top-picks'
import QuickNavigation from './products-list-header/quickNavigation'
import RankEUSection from './sections/rank-section-eu-layout'
import ShippingEUSection from './sections/shipping-section-eu-layout'
import WhyAmazonEUSection from './sections/why-amazon-section-eu-layout'
import ExploreEUSection from './sections/explore-section-eu-layout'
import ListHeader from './products-list-header'
import {getListSectionVisibility} from './section-visibility'
import LastPurchases from './sections/last-purchases-section'
import ReviewCommentSection from './sections/review-comment-section'
import TrustUsBrandsSection from './sections/trust-us-brands-section'

export default function IndexRoute() {
  const {featureFlags, googleAdsKey, deviceType, domain, isUSLocale, language, forcedLanguage, locale} =
    useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse,
    sortBy: sortingOption,
    forcedPosition,
    forcedAsin,
    variation,
  } = useOutletContext<RootAndListContext>()
  const {
    isMetacriticList,
    listSource,
    productsCategory,
    products: responseProducts,
    sponsoredProducts,
    sponsoredBrowseNode,
    relatedProducts,
    faqs,
  } = dataResponse
  const {styles: stylesBydomain, domainName, domainType} = getDomainConfig(domain, locale)
  const translations = getTranslation(language)

  useReportAnalyticsEngaging(googleAdsKey)
  useReportClickoutToSiteTime()

  const fetcher = useFetcher({key: 'attributes'})
  const fetcherData = fetcher?.data as SlugLoaderResponse
  const attributesRef = useRef<HTMLDivElement>(null)
  const [sortBy, setSortBy] = useState<SortingOption | null>(sortingOption)
  const [productsToDisplay, setProductsToDisplay] = useState(responseProducts)

  const sortedProducts = isUSLocale ? getSortedProducts(productsToDisplay, sortBy) : productsToDisplay

  const [elementPosition, setElementPosition] = useState({
    position: 0,
    isSaved: false,
  })

  const showHustonProblemModal = featureFlags?.showHustonProblemPopup && deviceType === 'smartphone'
  const modalShowThresholdInSec = Number(featureFlags?.modalShowThresholdInSec ?? 5)

  const numberOfDwsItemsInMainList = responseProducts.reduce(
    (count, product) => count + (product.isSellerProduct ? 1 : 0),
    0,
  )
  const numberOfDwsItemsInOtherLists = sponsoredProducts.reduce(
    (count, product) => count + (product && product.isSellerProduct ? 1 : 0),
    0,
  )

  const isForcedPosition = !!forcedPosition && forcedPosition?.length > 0
  const isForcedAsin = !!forcedAsin && forcedAsin?.length > 0

  const [listRef] = useReportPageInfo({
    isMetacriticList: isMetacriticList ?? true,
    listSource: listSource,
    slug: productsCategory?.slug,
    numberOfItemsInMainList: responseProducts.length,
    numberOfItemsInOtherLists: sponsoredProducts.length,
    numberOfDwsItemsInMainList,
    numberOfDwsItemsInOtherLists,
    amazonBrowseNodeId: sponsoredBrowseNode?.id,
    amazonBrowseNodeName: sponsoredBrowseNode?.name,
    forcedPosition: isForcedPosition && isForcedAsin,
    asin: isForcedAsin ? forcedAsin : undefined,
    position: isForcedPosition ? forcedPosition : undefined,
    variation,
  })

  const bestDiscountedProduct = [...responseProducts, relatedProducts[0]].reduce(
    // @ts-expect-error there might be no products with discounts
    (max, p) => (p?.discount && p.discount > (max?.discount ?? -Infinity) ? p : max),
    undefined,
  )

  useEffect(() => {
    if (!elementPosition.isSaved && attributesRef.current) {
      const attributeWidgetPosition = attributesRef.current.getBoundingClientRect().top + window.scrollY
      setElementPosition((prev) => ({...prev, position: attributeWidgetPosition, isSaved: true}))
    }
  }, [elementPosition.isSaved])

  useEffect(() => {
    if (fetcherData) {
      setProductsToDisplay(fetcherData?.dataResponse?.products)
    }
  }, [fetcherData])

  const showTopPicksOnTop = isUSLocale && domainType === 'insider'

  const showTopPicksOnBottom =
    cssDomains.includes(domainName) ||
    (isUSLocale && domainType !== 'insider' && forcedLanguage?.toLowerCase() !== 'es')
  const showTopRatedListTitle = domainType === 'insider'
  const showRankSection = featureFlags?.showEngagingElements
  const {showSponsoredProducts, showRelatedProducts, showRelatedLists, showPopularCategories, showProductPageLinks} =
    getListSectionVisibility({variation, isUSLocale: !!isUSLocale, domainName, forcedLanguage})
  const showReasoningSection = isUSLocale && !featureFlags?.showEngagingElements
  const showBuyingGuide = forcedLanguage?.toLowerCase() !== 'es'
  const showFaqSection = isUSLocale && forcedLanguage?.toLowerCase() !== 'es'
  const showScrollOnTopButton = domainType === 'insider'
  const isMagaDomainType = domainType === 'maga'
  const isEUdomainType = domainType === 'eu'
  const showOptinmonster = Boolean(featureFlags?.enableOptinmonsterScript)

  function renderLayouts() {
    switch (true) {
      case isMagaDomainType:
        return (
          <>
            {showSponsoredProducts ? <SponsoredProducts className="flex justify-center mobile:hidden" /> : null}
            <CategoryProducts from={0} to={1} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <RankSection />
            {showSponsoredProducts ? (
              <SponsoredProducts className="justify-center desktop:hidden tablet:hidden mobile:flex" />
            ) : null}
            <CategoryProducts from={1} to={2} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <LastPurchases />
            <CategoryProducts from={2} to={3} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <ReviewCommentSection />
            <CategoryProducts from={3} to={4} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <TrustUsBrandsSection />
            <CategoryProducts from={4} to={10} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {sortedProducts?.length > 10 ? (
              <h3 className="mt-10 text-2xl font-bold text-gray-900">{translations.AdditionalProductsSection.title}</h3>
            ) : null}
            <CategoryProducts from={10} to={13} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <ExploreEUSection />
            <CategoryProducts from={13} to={15} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <TopPicks products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {showBuyingGuide ? <BuyingGuide /> : null}
            {showFaqSection ? (
              <FaqSection
                faqs={faqs}
                language={language}
                className="mb-10 mt-10 flex flex-col justify-center mobile:mt-0"
              />
            ) : null}
          </>
        )
      case isEUdomainType:
        return (
          <>
            <TrustBar />
            {showSponsoredProducts ? <SponsoredProducts className="flex justify-center mobile:hidden" /> : null}
            <CategoryProducts from={0} to={1} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <RankEUSection />
            {showSponsoredProducts ? (
              <SponsoredProducts className="justify-center desktop:hidden tablet:hidden mobile:flex" />
            ) : null}
            <CategoryProducts from={1} to={2} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <ShippingEUSection />
            <CategoryProducts from={2} to={3} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <WhyAmazonEUSection />
            <CategoryProducts from={3} to={10} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {sortedProducts?.length > 10 ? (
              <h3 className="mt-10 text-2xl font-bold text-gray-900">{translations.AdditionalProductsSection.title}</h3>
            ) : null}
            <CategoryProducts from={10} to={17} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <ExploreEUSection />
            <CategoryProducts from={17} to={20} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            <TopPicks products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {showBuyingGuide ? <BuyingGuide /> : null}
            {showFaqSection ? (
              <FaqSection
                faqs={faqs}
                language={language}
                className="mb-10 mt-10 flex flex-col justify-center mobile:mt-0"
              />
            ) : null}
          </>
        )
      default:
        return (
          <>
            {showTopPicksOnTop ? (
              <TopPicks products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            ) : null}
            {showTopPicksOnTop && <div id="topRatedList" />}
            {showTopRatedListTitle ? (
              <h2 className="text-2xl font-semibold text-black mobile:text-2xl">
                {translations.Header.top}&nbsp;
                {translations.Header.titleFormat(
                  sortedProducts.slice(0, 10).length,
                  productsCategory?.name,
                  featureFlags?.showEngagingElements,
                )}
              </h2>
            ) : null}
            {showSponsoredProducts ? <SponsoredProducts className="flex justify-center mobile:hidden" /> : null}
            <CategoryProducts from={0} to={1} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {showRankSection && <RankSection className="" />}
            {showSponsoredProducts ? (
              <SponsoredProducts className="justify-center desktop:hidden tablet:hidden mobile:flex" />
            ) : null}
            <CategoryProducts from={1} to={5} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {showRelatedProducts ? <RelatedProducts /> : null}
            <CategoryProducts from={5} to={10} products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            {showRelatedLists ? <RecommendedProducts /> : null}
            {showReasoningSection ? <ReasoningSection /> : null}
            {locale?.toLowerCase() === 'de' || showTopPicksOnBottom ? (
              <TopPicks products={sortedProducts} showProductPageLinks={showProductPageLinks} />
            ) : null}
            {showBuyingGuide ? <BuyingGuide /> : null}
            {showFaqSection ? (
              <FaqSection
                faqs={faqs}
                language={language}
                className="mb-10 mt-10 flex w-full flex-col justify-center mobile:mt-0"
              />
            ) : null}
            {showPopularCategories ? <PopularCategories /> : null}
          </>
        )
    }
  }

  return (
    <div
      className={clsx('pb-10', stylesBydomain.COMMONS_BODY, 'commons_body')}
      ref={listRef}
      data-testid="products-list"
    >
      <ListHeader sortBy={sortBy} setSortBy={setSortBy} />
      <div className={clsx('utils_main_container relative mx-auto', stylesBydomain.UTILS_MAIN_CONTAINER)}>
        {showTopPicksOnTop ? <QuickNavigation /> : null}
        <div className="flex flex-col gap-6">{renderLayouts()}</div>
      </div>
      {showOptinmonster ? (
        <OptinmonsterMetadata featureFlagVariation="none" dwsProduct={bestDiscountedProduct ?? relatedProducts[0]} />
      ) : null}
      {showHustonProblemModal ? <HustonProblemModal timeThresholdInSec={+modalShowThresholdInSec} /> : null}
      {showScrollOnTopButton ? <ScrollOnTop /> : null}
    </div>
  )
}
